import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Modal from '@material-ui/core/Modal';
import Divider from '@material-ui/core/Divider';
import Backdrop from '@material-ui/core/Backdrop';
import DoneIcon from '@material-ui/icons/Done';
import DeleteIcon from '@material-ui/icons/Delete';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { Form } from 'react-final-form';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';

import { EditableQuoteAnswerList } from '../QuoteQuestionFields';
import UnifiedAddService from '../../scheduled_work/UnifiedAddService';
import {
  EditService,
  EditServicesHeader,
} from '../../scheduled_work/UnifiedEditServices';

const useStyles = makeStyles(() => ({
  optionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  optionFooter: {
    marginTop: '10px',
    justifyContent: 'flex-end',
  },
  quoteOptionModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quoteOptionModalContent: {
    padding: '20px',
    minWidth: '500px',
    minHeight: '400px',
    maxWidth: '50%',
    width: '50%',
    maxHeight: '90%',
    overflow: 'scroll',
  },
  optionNameInput: {
    fontSize: 30,
  },
  optionLabelInput: {
    fontSize: 20,
    textAlign: 'right',
  },
  table: {
    display: 'table',
    width: '100%',
  },
  buttonIcon: {
    marginRight: '7px',
  },
}));

const QuoteOptionModal = ({ option, updateOption, deleteOption, onClose }) => {
  const classes = useStyles();
  const [isValid, setIsValid] = useState(true);
  const onChangeAnswer = (getNewAnswers) => {
    const newAnswers = getNewAnswers(option.answers);
    updateOption('answers', newAnswers);
  };
  const onClickClose = () => {
    if (isValid) {
      triggerFormSubmit();
      return onClose();
    }
    return null;
  };
  const onClickDelete = () => deleteOption();

  // Trigger the 'services' form submit externally
  // https://final-form.org/docs/react-final-form/faq
  const triggerFormSubmit = () => {
    document
      .getElementById('quoteOptionServicesForm')
      .dispatchEvent(new Event('submit', { cancelable: true }));
  };

  const updateQuoteOptionServices = ({ services, newServices = [] }) => {
    updateOption('services', services.concat(newServices));
  };

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.quoteOptionModal}
        open
        onClose={onClickClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Paper className={classes.quoteOptionModalContent}>
          <Grid display="flex" className={classes.optionHeader}>
            <Tooltip title="Click to edit option name" placement="left">
              <TextField
                placeholder={`Option ${option.optionIndex + 1}`}
                type="text"
                onChange={(e) => updateOption('name', e.target.value)}
                value={option.name}
                InputProps={{
                  className: classes.optionNameInput,
                  disableUnderline: true,
                }}
                label={null}
                multiline
              />
            </Tooltip>
            <Tooltip title="Click to edit option label" placement="right">
              <TextField
                placeholder="LABEL"
                type="text"
                onChange={(e) =>
                  updateOption('label', e.target.value.toUpperCase())
                }
                value={option.label}
                inputProps={{ className: classes.optionLabelInput }}
                InputProps={{ disableUnderline: true }}
                label={null}
                multiline
              />
            </Tooltip>
          </Grid>
          <Grid display="flex" md={8}>
            <Tooltip title="Click to edit option note" placement="left">
              <TextField
                placeholder="Add a note..."
                type="text"
                onChange={(e) => updateOption('note', e.target.value)}
                value={option.note}
                InputProps={{
                  disableUnderline: true,
                }}
                label={null}
                multiline
                fullWidth
              />
            </Tooltip>
          </Grid>
          <Typography variant="h6" gutterBottom>
            Services:
          </Typography>
          <TableContainer style={{ overflow: 'visible', marginBottom: '20px' }}>
            <TableBody className={classes.table}>
              <EditServicesHeader showUpsellStatus={false} showStatus={false} />
              <TableRow className="customList">
                <Form
                  onSubmit={updateQuoteOptionServices}
                  mutators={{
                    ...arrayMutators,
                  }}
                  initialValues={{ services: option.services }}
                  render={({
                    handleSubmit,
                    form: {
                      mutators: { push },
                    },
                  }) => (
                    <TableCell colSpan={9}>
                      <form
                        id="quoteOptionServicesForm"
                        onSubmit={handleSubmit}
                        onBlur={() => isValid && handleSubmit()}
                      >
                        {['services', 'newServices'].map((name) => (
                          <FieldArray name={name}>
                            {({ fields }) =>
                              fields.map((name, index) => (
                                <Box
                                  key={name}
                                  display="flex"
                                  alignItems="flex-start"
                                >
                                  <EditService
                                    index={index}
                                    source={name}
                                    record={null}
                                    fields={fields}
                                    showUpsellStatus={false}
                                    showStatus={false}
                                  />
                                </Box>
                              ))
                            }
                          </FieldArray>
                        ))}
                        <Grid container spacing={1}>
                          <Grid item md={8} sm={8} xs={8}>
                            <Box mt={1} className={classes.selectRoot}>
                              <UnifiedAddService push={push} />
                            </Box>
                          </Grid>
                        </Grid>
                      </form>
                    </TableCell>
                  )}
                />
              </TableRow>
            </TableBody>
          </TableContainer>
          {!!option?.answers?.length && (
            <>
              <Typography variant="h6" gutterBottom>
                Optional Information:
              </Typography>
              <EditableQuoteAnswerList
                answers={option.answers}
                setAnswers={onChangeAnswer}
                setIsValid={setIsValid}
              />
            </>
          )}
          <Box display="flex" className={classes.optionFooter}>
            <Button color="primary" onClick={onClickClose}>
              <DoneIcon fontSize="small" className={classes.buttonIcon} />
              Done
            </Button>
            <Button color="primary" onClick={onClickDelete}>
              <DeleteIcon
                color="error"
                fontSize="small"
                className={classes.buttonIcon}
              />
              Delete
            </Button>
          </Box>
        </Paper>
      </Modal>
    </div>
  );
};

export default QuoteOptionModal;
